import { useState, useCallback, useRef } from 'react';

/**
 * Global click-lock hook to prevent multiple button triggers (double submit, duplicate API calls).
 *
 * Usage:
 *   const [isLocked, runAction] = useActionLock();
 *   <Button disabled={isLocked} onClick={() => runAction(handleSave)}>Save</Button>
 *
 * Or with ActionButton (handles lock + spinner internally):
 *   <ActionButton onClick={handleSave}>Save</ActionButton>
 *
 * - On first click: lock is set immediately, async fn runs. Further clicks no-op while locked.
 * - Lock is released when the promise settles (success or failure).
 *
 * @returns {[boolean, function]} [isLocked, runAction]
 */
export function useActionLock() {
  const [isLocked, setIsLocked] = useState(false);
  const runningRef = useRef(false);

  const runAction = useCallback(
    async (asyncFn) => {
      if (typeof asyncFn !== 'function') return;
      if (runningRef.current) return;

      runningRef.current = true;
      setIsLocked(true);

      try {
        const result = await Promise.resolve(asyncFn());
        return result;
      } finally {
        runningRef.current = false;
        setIsLocked(false);
      }
    },
    []
  );

  return [isLocked, runAction];
}

export default useActionLock;
